import { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { fetchCdrs, syncCdrs, syncBandwidthCdrs, discoverBandwidthAccounts, getCallQuality, syncCallQuality } from '../services/portalApi';

function fmtDuration(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const str = iso.includes('T') ? iso : iso.replace(' ', 'T');
    const d = new Date(str.endsWith('Z') || str.includes('+') ? str : str + 'Z');
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ color: 'var(--muted)', marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

const STATUS_COLORS = {
  ANSWERED: 'var(--ok)', ANSWER: 'var(--ok)',
  BUSY: 'var(--warn)', FAILED: 'var(--err)',
  'NO ANSWER': 'var(--warn)', NOANSWER: 'var(--warn)',
};

const QUALITY_CONFIG = {
  good:    { color: 'var(--ok)', bg: 'var(--ok-bg)', border: '#14532d44', label: 'Good' },
  fair:    { color: 'var(--warn)', bg: 'var(--warn-bg)', border: '#92400e44', label: 'Fair' },
  poor:    { color: 'var(--err)', bg: 'var(--err-bg)', border: 'var(--err-border)', label: 'Poor' },
  unknown: { color: 'var(--muted)', bg: 'var(--border)', border: 'var(--border-strong)',   label: '—'    },
};

function QualityBadge({ label }) {
  const cfg = QUALITY_CONFIG[label] || QUALITY_CONFIG.unknown;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: 11, fontWeight: 600, background: cfg.bg,
      color: cfg.color, border: `1px solid ${cfg.border}`,
    }}>{cfg.label}</span>
  );
}

function MosBadge({ mos }) {
  if (mos == null) return <span style={{ color: 'var(--muted-3)' }}>—</span>;
  const color = mos >= 4.0 ? 'var(--ok)' : mos >= 3.5 ? 'var(--warn)' : 'var(--err)';
  return <span style={{ fontFamily: 'monospace', fontSize: 12, color, fontWeight: 600 }}>{mos.toFixed(2)}</span>;
}

const HANGUP_SOURCE_MAP = {
  CALLING_PARTY: { label: 'Caller',       color: 'var(--info)' },
  CALLED_PARTY:  { label: 'Called Party', color: 'var(--purple)' },
  NETWORK:       { label: 'Network',      color: '#f97316' },
};

function HangupSource({ source }) {
  if (!source) return <span style={{ color: 'var(--muted-3)', fontSize: 11 }}>—</span>;
  const cfg = HANGUP_SOURCE_MAP[source] || { label: source, color: 'var(--muted)' };
  return <span style={{ fontSize: 11, fontWeight: 600, color: cfg.color }}>{cfg.label}</span>;
}

// ITU-T E-model simplified MOS estimate from network metrics
function estimateMos(jitterMs, latencyMs, packetLossPct) {
  if (jitterMs == null && latencyMs == null && packetLossPct == null) return null;
  const j = jitterMs     ?? 0;
  const l = latencyMs    ?? 0;
  const p = packetLossPct ?? 0;
  const Id = 0.024 * l + 0.11 * Math.max(0, l - 177.3);
  const Ie = p * 2.5;
  const Ij = j * 0.4;
  let R = 93.2 - Id - Ie - Ij;
  R = Math.max(0, Math.min(100, R));
  const mos = 1 + 0.035 * R + 7e-6 * R * (R - 60) * (100 - R);
  return Math.max(1.0, Math.min(5.0, Math.round(mos * 100) / 100));
}

function mosColor(mos) {
  if (mos == null) return 'var(--muted-3)';
  if (mos >= 4.0) return 'var(--ok)';
  if (mos >= 3.5) return '#84cc16';
  if (mos >= 3.0) return 'var(--warn)';
  if (mos >= 2.0) return '#f97316';
  return 'var(--err)';
}

function CdrQuality({ jitter, latency, packetLoss, pdd }) {
  const mos = estimateMos(jitter, latency, packetLoss);

  if (mos != null) {
    const color = mosColor(mos);
    const label = mos >= 4.0 ? 'Good' : mos >= 3.5 ? 'Fair' : mos >= 3.0 ? 'Poor' : 'Bad';
    const tip = [
      jitter     != null ? `Jitter: ${jitter}ms`                   : null,
      latency    != null ? `Latency: ${latency}ms`                  : null,
      packetLoss != null ? `Packet loss: ${packetLoss.toFixed(2)}%` : null,
    ].filter(Boolean).join('\n');
    return (
      <span title={tip} style={{ cursor: 'default' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color }}>{mos.toFixed(2)}</span>
        <span style={{ fontSize: 10, color, marginLeft: 4, opacity: 0.75 }}>{label}</span>
      </span>
    );
  }

  // Fall back to post-dial delay when MOS metrics aren't available
  if (pdd != null) {
    const color = pdd < 500 ? 'var(--ok)' : pdd < 2000 ? 'var(--warn)' : 'var(--err)';
    const label = pdd < 500 ? 'Fast' : pdd < 2000 ? 'Normal' : 'Slow';
    return (
      <span title={`Post-dial delay: ${pdd}ms`} style={{ cursor: 'default' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color }}>{pdd}ms</span>
        <span style={{ fontSize: 10, color, marginLeft: 4, opacity: 0.75 }}>{label}</span>
      </span>
    );
  }

  return <span style={{ color: 'var(--muted-3)', fontSize: 11 }}>—</span>;
}

const PAGE_SIZE = 50;

export default function Analytics({ user, viewAsCustomer }) {
  // ── Voxbone CDRs ────────────────────────────────────────────────────────────
  const [cdrs,        setCdrs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [syncing,     setSyncing]     = useState(false);
  const [error,       setError]       = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [search,      setSearch]      = useState('');
  const [page,        setPage]        = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const data = await fetchCdrs({ limit: 2000, since });
      if (data.available === false) {
        setUnavailable(true);
        setCdrs([]);
      } else {
        setCdrs(data.cdrs || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncCdrs(7);
      setLoading(true);
      await load();
    } catch (err) {
      alert('Sync failed: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const [discoveringBw, setDiscoveringBw] = useState(false);
  const handleDiscoverBandwidth = async () => {
    setDiscoveringBw(true);
    try {
      const result = await discoverBandwidthAccounts();
      const msg = `Discovery complete.\n\nFound: ${result.discovered} sub-accounts\nCreated: ${result.created} new customers\nAlready existed: ${result.existing}`;
      alert(msg);
    } catch (err) {
      alert('Discovery failed: ' + err.message);
    } finally {
      setDiscoveringBw(false);
    }
  };

  const [syncingBw, setSyncingBw] = useState(false);
  const handleSyncBandwidth = async () => {
    setSyncingBw(true);
    try {
      const result = await syncBandwidthCdrs();
      setLoading(true);
      await load();
      if (result?.count === 0) alert('Bandwidth sync returned 0 records. Check that your account has CDR access and calls in the last 7 days.');
    } catch (err) {
      alert('Bandwidth sync failed: ' + err.message);
    } finally {
      setSyncingBw(false);
    }
  };

  const hasBwCdrs = cdrs.some(c => c.source === 'bandwidth');

  const filtered = search
    ? cdrs.filter(c =>
        c.from_number?.includes(search) ||
        c.to_number?.includes(search) ||
        c.status?.toLowerCase().includes(search.toLowerCase()) ||
        c.source?.toLowerCase().includes(search.toLowerCase())
      )
    : cdrs;

  const total    = filtered.length;
  const answered = filtered.filter(c => /answer|complete/i.test(c.status)).length;
  const totalSec = filtered.reduce((s, c) => s + (c.duration || 0), 0);

  const hourMap = {};
  for (const c of cdrs) {
    const ts = c.started_at || c.start_time;
    if (!ts) continue;
    const hour = (ts.includes('T') ? ts : ts.replace(' ', 'T')).slice(0, 13);
    if (!hourMap[hour]) hourMap[hour] = { hour, inbound: 0, outbound: 0 };
    if (/outbound/i.test(c.direction)) hourMap[hour].outbound++;
    else hourMap[hour].inbound++;
  }
  const dailyData = Object.values(hourMap)
    .sort((a, b) => a.hour.localeCompare(b.hour))
    .slice(-24)
    .map(d => ({
      ...d,
      day: new Date(d.hour + ':00:00Z').toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
    }));

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows  = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ── Teams Call Quality ───────────────────────────────────────────────────────
  const [quality,       setQuality]       = useState([]);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [qualityErr,    setQualityErr]    = useState(null);
  const [syncingQuality, setSyncingQuality] = useState(false);
  const [qualitySearch,  setQualitySearch]  = useState('');
  const [qualityPage,    setQualityPage]    = useState(0);

  const effectiveCid = viewAsCustomer?.id ?? (user?.isAdmin ? null : user?.id);

  const loadQuality = useCallback(async () => {
    if (!effectiveCid) return;
    setQualityLoading(true);
    setQualityErr(null);
    try {
      const data = await getCallQuality(effectiveCid, 30);
      setQuality(data.records || []);
    } catch (e) {
      setQualityErr(e.message);
    } finally {
      setQualityLoading(false);
    }
  }, [effectiveCid]);

  useEffect(() => { loadQuality(); }, [loadQuality]);

  const handleSyncQuality = async () => {
    if (!effectiveCid) return;
    setSyncingQuality(true);
    setQualityErr(null);
    try {
      await syncCallQuality(effectiveCid);
      await loadQuality();
    } catch (e) {
      if (e.message?.includes('403') || e.message?.includes('Missing')) {
        setQualityErr('Missing permission: CallRecords.Read.All — add this Application permission to the Azure AD app registration and grant admin consent.');
      } else {
        setQualityErr(e.message);
      }
    } finally {
      setSyncingQuality(false);
    }
  };

  // Quality stats
  const withMos     = quality.filter(r => r.avg_mos != null);
  const avgMos      = withMos.length ? withMos.reduce((s, r) => s + r.avg_mos, 0) / withMos.length : null;
  const goodCalls   = quality.filter(r => r.quality_label === 'good').length;
  const fairCalls   = quality.filter(r => r.quality_label === 'fair').length;
  const poorCalls   = quality.filter(r => r.quality_label === 'poor').length;
  const avgPl       = withMos.length ? withMos.reduce((s, r) => s + (r.avg_packet_loss || 0), 0) / withMos.length : null;

  // Daily MOS trend
  const mosDayMap = {};
  for (const r of quality) {
    if (!r.start_time || r.avg_mos == null) continue;
    const day = r.start_time.slice(0, 10);
    if (!mosDayMap[day]) mosDayMap[day] = { day, total: 0, count: 0 };
    mosDayMap[day].total += r.avg_mos;
    mosDayMap[day].count++;
  }
  const mosTrendData = Object.values(mosDayMap)
    .sort((a, b) => a.day.localeCompare(b.day))
    .slice(-14)
    .map(d => ({
      day: new Date(d.day + 'T12:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      mos: Math.round(d.total / d.count * 100) / 100,
    }));

  // Filtered quality table
  const filteredQuality = qualitySearch
    ? quality.filter(r =>
        r.organizer_name?.toLowerCase().includes(qualitySearch.toLowerCase()) ||
        r.organizer_upn?.toLowerCase().includes(qualitySearch.toLowerCase()) ||
        r.quality_label?.includes(qualitySearch.toLowerCase())
      )
    : quality;
  const qPageCount = Math.max(1, Math.ceil(filteredQuality.length / PAGE_SIZE));
  const qPageRows  = filteredQuality.slice(qualityPage * PAGE_SIZE, (qualityPage + 1) * PAGE_SIZE);

  const needsCustomer = user?.isAdmin && !viewAsCustomer;

  return (
    <div className="content">
      {/* ── Voxbone CDR Stats ── */}
      <div className="stats-grid mb-20">
        {[
          { label: 'Total Calls (24h)', value: loading ? '…' : total.toLocaleString() },
          { label: 'Answered',             value: loading ? '…' : answered.toLocaleString() },
          { label: 'Answer Rate',          value: loading ? '…' : (total ? `${Math.round(answered / total * 100)}%` : '—') },
          { label: 'Total Talk Time',      value: loading ? '…' : fmtDuration(totalSec) },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Daily Call Volume ── */}
      <div className="card mb-20">
        <div className="card-header">
          <div className="card-title">Hourly Call Volume — Last 24 Hours</div>
          {user?.isAdmin && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm" onClick={handleSync} disabled={syncing || loading || syncingBw}>
                {syncing ? 'Syncing…' : 'Sync Voxbone'}
              </button>
              <button className="btn btn-sm" onClick={handleSyncBandwidth} disabled={syncingBw || syncing || loading || discoveringBw}>
                {syncingBw ? 'Syncing…' : 'Sync Bandwidth'}
              </button>
              <button className="btn btn-sm" onClick={handleDiscoverBandwidth} disabled={discoveringBw || syncingBw || syncing || loading} title="Fetch sub-accounts from Bandwidth and create them as portal customers">
                {discoveringBw ? 'Discovering…' : 'Import BW Sub-accounts'}
              </button>
            </div>
          )}
        </div>
        <div className="card-body">
          {loading ? (
            <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '40px 0' }}>Loading call history…</div>
          ) : error ? (
            <div style={{ textAlign: 'center', color: 'var(--err)', padding: '40px 0' }}>{error}</div>
          ) : unavailable ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ color: 'var(--warn)', marginBottom: 8, fontSize: 14 }}>CDR access not available</div>
              <div style={{ color: 'var(--muted-3)', fontSize: 12 }}>
                The Voxbone API returned 403 on all CDR endpoints.<br/>
                Contact Voxbone to enable CDR reporting on your account.
              </div>
            </div>
          ) : dailyData.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '40px 0' }}>
              No call data in the last 24 hours.{user?.isAdmin ? ' Use Sync Voxbone or Sync Bandwidth to fetch records.' : ''}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dailyData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                <XAxis dataKey="day" tick={{ fill: 'var(--muted-3)', fontSize: 11 }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fill: 'var(--muted-3)', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip content={<CustomTooltip/>} cursor={{ fill: '#ffffff08' }}/>
                <Bar dataKey="inbound"  name="Inbound"  fill="var(--purple)" radius={[3,3,0,0]}/>
                <Bar dataKey="outbound" name="Outbound" fill="var(--ok)" radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Teams Call Quality ── */}
      <div className="card mb-20">
        <div className="card-header">
          <div>
            <div className="card-title">Teams Call Quality — Last 30 Days</div>
            {!needsCustomer && quality.length > 0 && (
              <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 2 }}>
                {quality.length} call{quality.length !== 1 ? 's' : ''} · data from Microsoft Graph Call Records API
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!needsCustomer && (
              <button className="btn btn-sm" onClick={handleSyncQuality} disabled={syncingQuality}>
                {syncingQuality
                  ? <><RefreshCw size={11} style={{ animation: 'spin 0.8s linear infinite' }}/> Syncing…</>
                  : 'Sync from M365'}
              </button>
            )}
          </div>
        </div>

        {needsCustomer ? (
          <div className="card-body" style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '40px 0', fontSize: 13 }}>
            Select a customer to view Teams call quality data.
          </div>
        ) : qualityErr ? (
          <div className="card-body">
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--err-bg)', border: '1px solid var(--err-border)', borderRadius: 8, padding: '12px 14px' }}>
              <AlertTriangle size={16} color="var(--err)" style={{ flexShrink: 0, marginTop: 1 }}/>
              <div>
                <div style={{ fontSize: 13, color: 'var(--err)', fontWeight: 600, marginBottom: 4 }}>Sync failed</div>
                <div style={{ fontSize: 12, color: 'var(--err)' }}>{qualityErr}</div>
              </div>
            </div>
          </div>
        ) : qualityLoading ? (
          <div className="card-body" style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '40px 0' }}>Loading…</div>
        ) : quality.length === 0 ? (
          <div className="card-body" style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ color: 'var(--muted-3)', fontSize: 13, marginBottom: 8 }}>No call quality data yet.</div>
            <div style={{ fontSize: 12, color: 'var(--muted-3)' }}>
              Click <strong style={{ color: 'var(--info)' }}>Sync from M365</strong> to pull call records from the connected Microsoft 365 tenant.
              <br/>Requires the <code style={{ color: 'var(--purple)' }}>CallRecords.Read.All</code> permission on the app registration.
            </div>
          </div>
        ) : (
          <>
            {/* Quality stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              {[
                { label: 'Total Teams Calls', value: quality.length.toLocaleString() },
                { label: 'Average MOS Score', value: avgMos != null ? avgMos.toFixed(2) : '—', color: avgMos != null ? (avgMos >= 4.0 ? 'var(--ok)' : avgMos >= 3.5 ? 'var(--warn)' : 'var(--err)') : 'var(--muted)' },
                { label: 'Good / Fair / Poor', value: `${goodCalls} / ${fairCalls} / ${poorCalls}` },
                { label: 'Avg Packet Loss', value: avgPl != null ? `${(avgPl * 100).toFixed(2)}%` : '—' },
              ].map(s => (
                <div key={s.label}>
                  <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: s.color || 'var(--text-2)' }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* MOS trend chart */}
            {mosTrendData.length > 1 && (
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, color: 'var(--muted-2)', marginBottom: 10 }}>Daily Average MOS Score (5.0 = perfect)</div>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={mosTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                    <XAxis dataKey="day" tick={{ fill: 'var(--muted-3)', fontSize: 11 }} axisLine={false} tickLine={false}/>
                    <YAxis domain={[2.5, 5]} tick={{ fill: 'var(--muted-3)', fontSize: 11 }} axisLine={false} tickLine={false}/>
                    <Tooltip content={<CustomTooltip/>} cursor={{ stroke: 'var(--border-strong)' }}/>
                    <Line
                      type="monotone" dataKey="mos" name="Avg MOS"
                      stroke="var(--purple)" strokeWidth={2} dot={{ fill: 'var(--purple)', r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Quality table */}
            <div className="card-header" style={{ borderTop: 'none', paddingTop: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--muted-2)' }}>Recent Calls</div>
              <input
                type="text"
                placeholder="Search organizer or quality…"
                value={qualitySearch}
                onChange={e => { setQualitySearch(e.target.value); setQualityPage(0); }}
                style={{
                  background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6,
                  color: 'var(--text-2)', padding: '5px 10px', fontSize: 12, width: 220,
                }}
              />
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date / Time</th>
                    <th>Type</th>
                    <th>Organizer</th>
                    <th>Participants</th>
                    <th>Duration</th>
                    <th>Modalities</th>
                    <th>MOS</th>
                    <th>Packet Loss</th>
                    <th>Jitter</th>
                    <th>Quality</th>
                  </tr>
                </thead>
                <tbody>
                  {qPageRows.map((r, i) => {
                    let mods = [];
                    try { mods = JSON.parse(r.modalities || '[]'); } catch {}
                    return (
                      <tr key={r.id || i}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtTime(r.start_time)}</td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {r.call_type === 'peerToPeer' ? 'P2P' : r.call_type === 'groupCall' ? 'Group' : r.call_type || '—'}
                        </td>
                        <td>
                          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.organizer_name || '—'}</div>
                          {r.organizer_upn && <div style={{ fontSize: 10, color: 'var(--muted-3)' }}>{r.organizer_upn}</div>}
                        </td>
                        <td style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>{r.participant_cnt || '—'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtDuration(r.duration_sec)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {mods.map(m => (
                              <span key={m} style={{
                                fontSize: 10, padding: '1px 6px', borderRadius: 8,
                                background: 'var(--border)', color: 'var(--muted)', border: '1px solid var(--border-strong)',
                              }}>{m}</span>
                            ))}
                          </div>
                        </td>
                        <td><MosBadge mos={r.avg_mos}/></td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: r.avg_packet_loss > 0.05 ? 'var(--err)' : r.avg_packet_loss > 0.01 ? 'var(--warn)' : 'var(--muted)' }}>
                          {r.avg_packet_loss != null ? `${(r.avg_packet_loss * 100).toFixed(2)}%` : '—'}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: r.avg_jitter_ms > 30 ? 'var(--err)' : 'var(--muted)' }}>
                          {r.avg_jitter_ms != null ? `${r.avg_jitter_ms}ms` : '—'}
                        </td>
                        <td><QualityBadge label={r.quality_label}/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {qPageCount > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '12px 20px', borderTop: '1px solid var(--border)', fontSize: 12 }}>
                <button
                  onClick={() => setQualityPage(p => Math.max(0, p - 1))}
                  disabled={qualityPage === 0}
                  style={{ background: 'none', border: '1px solid var(--border-strong)', color: 'var(--muted)', borderRadius: 4, padding: '3px 10px', cursor: qualityPage === 0 ? 'default' : 'pointer' }}
                >←</button>
                <span style={{ color: 'var(--muted-3)' }}>Page {qualityPage + 1} of {qPageCount}</span>
                <button
                  onClick={() => setQualityPage(p => Math.min(qPageCount - 1, p + 1))}
                  disabled={qualityPage === qPageCount - 1}
                  style={{ background: 'none', border: '1px solid var(--border-strong)', color: 'var(--muted)', borderRadius: 4, padding: '3px 10px', cursor: qualityPage === qPageCount - 1 ? 'default' : 'pointer' }}
                >→</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── CDR table ── */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Call Detail Records</div>
          <input
            type="text"
            placeholder="Search number or status…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            style={{
              background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6,
              color: 'var(--text-2)', padding: '5px 10px', fontSize: 12, width: 220,
            }}
          />
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Source</th>
                <th>Direction</th>
                <th>From</th>
                <th>To</th>
                <th>Duration</th>
                <th>Status</th>
                {hasBwCdrs && <th>Hung Up By</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={hasBwCdrs ? 8 : 7} style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '24px 0' }}>Loading…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={hasBwCdrs ? 8 : 7} style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '24px 0' }}>No records found</td></tr>
              ) : pageRows.map((c, i) => {
                const isBusy      = /busy/i.test(c.hangup_cause || '');
                const statusColor = isBusy
                  ? 'var(--ok)'
                  : (STATUS_COLORS[(c.status || '').toUpperCase()] || 'var(--muted)');
                const statusLabel = isBusy
                  ? 'Busy'
                  : (c.hangup_cause || c.status || '—');
                const isOut = /outbound/i.test(c.direction);
                const isBw  = c.source === 'bandwidth';
                const ts    = c.started_at || c.start_time;
                return (
                  <tr key={c.id || i}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtTime(ts)}</td>
                    <td>
                      <span style={{
                        fontSize: 10, padding: '1px 7px', borderRadius: 8, fontWeight: 600,
                        background: isBw ? 'var(--purple-bg)' : 'var(--info-bg)',
                        color: isBw ? 'var(--purple)' : 'var(--info)',
                        border: `1px solid ${isBw ? 'var(--purple-border)' : 'var(--info-border)'}`,
                      }}>{isBw ? 'Bandwidth' : 'Voxbone'}</span>
                    </td>
                    <td>
                      <span style={{ fontSize: 11, color: isOut ? 'var(--ok)' : 'var(--purple)', fontWeight: 600 }}>
                        {(c.direction || 'inbound').toUpperCase()}
                      </span>
                    </td>
                    <td><span className="tag font-mono">{c.from_number || c.caller_number || '—'}</span></td>
                    <td><span className="tag font-mono">{c.to_number || c.called_number || '—'}</span></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtDuration(c.duration)}</td>
                    <td>
                      <span style={{ fontSize: 11, color: statusColor, fontWeight: 600 }}>
                        {statusLabel}
                      </span>
                    </td>
                    {hasBwCdrs && (
                      <td>{isBw ? <HangupSource source={c.hangup_source}/> : <span style={{ color: 'var(--muted-3)', fontSize: 11 }}>—</span>}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pageCount > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '12px 20px', borderTop: '1px solid var(--border)', fontSize: 12 }}>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{ background: 'none', border: '1px solid var(--border-strong)', color: 'var(--muted)', borderRadius: 4, padding: '3px 10px', cursor: page === 0 ? 'default' : 'pointer' }}
            >←</button>
            <span style={{ color: 'var(--muted-3)' }}>Page {page + 1} of {pageCount} · {filtered.length} records</span>
            <button
              onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
              disabled={page === pageCount - 1}
              style={{ background: 'none', border: '1px solid var(--border-strong)', color: 'var(--muted)', borderRadius: 4, padding: '3px 10px', cursor: page === pageCount - 1 ? 'default' : 'pointer' }}
            >→</button>
          </div>
        )}
      </div>
    </div>
  );
}
